import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";
import Toggle from "../../components/Toggle";

const EMPTY = { title: "", subtitle: "", image_url: "", link: "", active: true, order: 0 };

export default function AdminPromotions({ onChanged }) {
  const [banners, setBanners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/banners?all=true").then((r) => setBanners(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const refreshAll = () => { load(); onChanged?.(); };

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (b) => { setEditing(b.id); setForm({ ...b }); setShowForm(true); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: parseInt(form.order) || 0 };
    try {
      if (editing) await api.put(`/banners/${editing}`, payload);
      else await api.post("/banners", payload);
      toast.success(editing ? "Banner updated" : "Banner created");
      setShowForm(false);
      refreshAll();
    } catch { toast.error("Could not save banner"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    await api.delete(`/banners/${id}`);
    toast.success("Banner deleted");
    refreshAll();
  };

  return (
    <div className="mt-6">
      <button data-testid="admin-add-banner" onClick={openNew} className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-white px-4 py-2.5 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors mb-4">
        <Plus className="w-4 h-4" /> Add Promo Banner
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {banners.length === 0 && <p className="text-[#5C665D]">No promo banners yet.</p>}
        {banners.map((b) => (
          <div key={b.id} data-testid={`admin-banner-${b.id}`} className={`bg-white border border-[#E8E4D9] rounded-xl overflow-hidden ${b.active ? "" : "opacity-60"}`}>
            <div className="h-28 bg-[#F4EFE6]">{b.image_url && <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" />}</div>
            <div className="p-4 flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-display font-medium text-[#1A231C] truncate">{b.title || "(untitled)"}</p>
                <p className="text-xs text-[#5C665D] truncate">{b.link || "no link"} · {b.active ? "Active" : "Hidden"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`banner-edit-${b.id}`} onClick={() => openEdit(b)} className="text-[var(--brand-primary)] p-1.5 rounded hover:bg-[#F4EFE6]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`banner-delete-${b.id}`} onClick={() => del(b.id)} className="text-[var(--brand-accent)] p-1.5 rounded hover:bg-[#F4EFE6]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-lg text-[#1A231C]">{editing ? "Edit Banner" : "New Banner"}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-[#5C665D]" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="banner-form-title" value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Title" className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
              <input data-testid="banner-form-subtitle" value={form.subtitle} onChange={(e) => setField("subtitle", e.target.value)} placeholder="Subtitle" className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
              <input data-testid="banner-form-link" value={form.link} onChange={(e) => setField("link", e.target.value)} placeholder="Click-through link (e.g. /shop?category=food)" className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
              <input data-testid="banner-form-order" type="number" value={form.order} onChange={(e) => setField("order", e.target.value)} placeholder="Order" className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
              <ImageUploader label="Banner Image" testid="banner-image" value={form.image_url} onChange={(v) => setField("image_url", v)} />
              <div className="flex items-center justify-between border border-[#E8E4D9] rounded-lg px-4 py-2.5">
                <span className="text-sm text-[#1A231C]">Active (visible on homepage)</span>
                <Toggle checked={form.active} onChange={(v) => setField("active", v)} testid="banner-form-active" />
              </div>
            </div>
            <button data-testid="banner-form-save" type="submit" className="w-full mt-5 bg-[var(--brand-primary)] text-white px-6 py-3 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors">
              {editing ? "Update" : "Create"} Banner
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
